import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Clock, Plus, Search } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { EmptyState, Panel, RouteLine, Skeleton, StatusBadge, T, money } from "@/components/admin/ui";

// Ordre opérationnel : ce dont l'équipe a besoin d'abord.
const VIEWS = [
  ["live", "En cours"], ["today", "Aujourd'hui"], ["upcoming", "À venir"],
  ["unassigned", "Sans chauffeur"], ["completed", "Terminées"], ["cancelled", "Annulées"],
  ["all", "Toutes"], ["abandoned", "Abandons"],
];
const GROUPED_VIEWS = new Set(["live", "today", "upcoming", "unassigned", "all"]);
const SPOTLIGHT_VIEWS = new Set(["live", "today", "upcoming"]);
const LIVE_STATUSES = ["driver_on_the_way", "driver_arrived", "on_board"];

const DAY_FMT = new Intl.DateTimeFormat("fr-CH", { weekday: "long", day: "numeric", month: "long" });
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const isoToday = () => toIso(new Date());
const isoTomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return toIso(d); };

function dayLabel(dateStr) {
  if (!dateStr) return "";
  if (dateStr === isoToday()) return "Aujourd'hui";
  if (dateStr === isoTomorrow()) return "Demain";
  const label = DAY_FMT.format(new Date(`${dateStr}T12:00:00`));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function minutesUntil(b) {
  const dt = new Date(`${b.date}T${b.time || "00:00"}:00`);
  return Math.round((dt - Date.now()) / 60000);
}

function countdownLabel(b) {
  const mins = minutesUntil(b);
  if (LIVE_STATUSES.includes(b.status)) return "en course maintenant";
  if (mins <= 0) return "maintenant";
  if (mins < 60) return `dans ${mins} min`;
  const h = Math.floor(mins / 60), m = mins % 60;
  if (h < 24) return m ? `dans ${h} h ${String(m).padStart(2, "0")}` : `dans ${h} h`;
  return null;
}

// Code couleur d'urgence : impossible de rater une course qui approche.
function urgency(b) {
  if (LIVE_STATUSES.includes(b.status)) return "live";
  const unassigned = !b.driver?.first_name;
  if (unassigned && (b.date === isoToday() || b.date === isoTomorrow())) return "urgent";
  if (b.date === isoToday()) return "today";
  return null;
}

const URGENCY_ROW = {
  live: "border-l-4 border-l-emerald-500 bg-emerald-50/50",
  urgent: "border-l-4 border-l-amber-500 bg-amber-50/60",
  today: "border-l-4 border-l-miles/60 bg-miles-ice/25",
  none: "border-l-4 border-l-transparent",
};
const URGENCY_CARD = {
  live: "border-l-4 border-l-emerald-500",
  urgent: "border-l-4 border-l-amber-500",
  today: "border-l-4 border-l-miles/60",
  none: "",
};

function isUnpaid(b) {
  return b.payment_status && !["paid", "succeeded"].includes(b.payment_status);
}

export default function AdminBookings() {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "upcoming";
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState(null);
  const [, setTick] = useState(0); // relance le rendu pour les comptes à rebours

  const fetchData = () => {
    adminApi.get("/bookings", { params: { view, q, page, per_page: 25 } })
      .then((r) => setData(r.data)).catch(() => {});
    adminApi.get("/bookings-counts").then((r) => setCounts(r.data)).catch(() => {});
  };

  useEffect(() => {
    const timer = setTimeout(fetchData, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [view, q, page]);

  // auto-rafraîchissement : une nouvelle réservation ne passe jamais inaperçue
  useEffect(() => {
    const iv = setInterval(() => { fetchData(); setTick((n) => n + 1); }, 30000);
    const onFocus = () => fetchData();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [view, q, page]);

  useEffect(() => { setPage(1); }, [view, q]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1;
  const grouped = GROUPED_VIEWS.has(view);

  const rows = useMemo(() => {
    if (!data) return [];
    if (!grouped) return data.bookings.map((b) => ({ booking: b }));
    const out = [];
    let lastDate = null;
    for (const b of data.bookings) {
      if (b.date !== lastDate) {
        out.push({ day: b.date });
        lastDate = b.date;
      }
      out.push({ booking: b });
    }
    return out;
  }, [data, grouped]);

  const spotlight = !q && SPOTLIGHT_VIEWS.has(view) && data?.bookings?.length ? data.bookings[0] : null;

  return (
    <div className="space-y-5" data-testid="admin-bookings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className={T.pageTitle}>Réservations</h1>
          <p className="mt-1 text-sm text-slate">{data ? `${data.total} réservation${data.total > 1 ? "s" : ""}` : "…"} · vue {VIEWS.find(([v]) => v === view)?.[1]?.toLowerCase()} · actualisé automatiquement</p>
        </div>
        <Link to="/admin/app/bookings/new" data-testid="bookings-new-button"
          className="inline-flex h-10 items-center gap-2 rounded-xl bg-miles px-4 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(36,87,255,0.25)] transition-all duration-150 hover:bg-miles-dark active:scale-[0.98]">
          <Plus className="h-4 w-4" strokeWidth={2} /> Nouvelle réservation
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-0.5 rounded-xl border border-ink/[0.07] bg-white p-1" data-testid="bookings-views">
          {VIEWS.map(([id, label]) => {
            const count = counts?.[id];
            return (
              <button key={id} type="button" onClick={() => setParams({ view: id })}
                data-testid={`view-${id}`}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] transition-all duration-150 ${view === id ? "bg-miles-ice font-semibold text-ink" : "text-slate hover:text-ink"}`}>
                {label}
                {count > 0 && (
                  <span data-testid={`view-count-${id}`}
                    className={`rounded-full px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none ${view === id ? "bg-miles text-white" : id === "unassigned" ? "bg-amber-100 text-amber-700" : id === "live" ? "bg-emerald-100 text-emerald-700" : "bg-ink/[0.06] text-slate"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/60" strokeWidth={1.5} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, client, trajet, vol…"
            data-testid="bookings-search" className={`${T.input} h-10 pl-10 text-sm`} />
        </div>
      </div>

      {spotlight && <NextRideCard b={spotlight} />}

      <Panel testId="bookings-list" padded={false}>
        {!data && <div className="p-5"><Skeleton rows={5} /></div>}
        {data && data.bookings.length === 0 && (
          <EmptyState title="Aucune réservation dans cette vue"
            hint="Modifiez les filtres ou créez une réservation manuelle."
            testId="bookings-empty"
            action={<Link to="/admin/app/bookings/new" className="text-sm font-semibold text-miles hover:underline">Créer une réservation</Link>} />
        )}
        {data && data.bookings.length > 0 && (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-slate/60">
                    <th className="px-5 pb-2 pt-4">Horaire</th>
                    <th className="px-3 pb-2 pt-4">Trajet</th>
                    <th className="px-3 pb-2 pt-4">Client</th>
                    <th className="px-3 pb-2 pt-4">Chauffeur</th>
                    <th className="px-3 pb-2 pt-4">Statut</th>
                    <th className="px-5 pb-2 pt-4 text-right">Prix</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => row.day ? (
                    <tr key={`day-${row.day}-${i}`} data-testid={`day-header-${row.day}`}>
                      <td colSpan={6} className={`px-5 py-2 ${row.day === isoToday() ? "bg-miles-ice/70" : "bg-cloud/70"} border-y border-ink/[0.06]`}>
                        <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${row.day === isoToday() ? "text-miles" : "text-slate"}`}>{dayLabel(row.day)}</span>
                      </td>
                    </tr>
                  ) : (
                    <BookingRow key={row.booking.id} b={row.booking} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-2 p-4 md:hidden">
              {rows.map((row, i) => row.day ? (
                <p key={`day-${row.day}-${i}`} data-testid={`day-header-m-${row.day}`}
                  className={`pt-2 text-[11px] font-semibold uppercase tracking-[0.14em] ${row.day === isoToday() ? "text-miles" : "text-slate"}`}>{dayLabel(row.day)}</p>
              ) : (
                <BookingCard key={row.booking.id} b={row.booking} />
              ))}
            </div>
            <div className="flex items-center justify-between px-5 pb-4 pt-2 text-sm">
              <span className={T.meta}>{data.total} résultat(s) · page {page}/{totalPages}</span>
              <div className="flex gap-1.5">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"
                  className="h-8 w-8 rounded-lg border border-ink/10 text-slate transition-colors hover:border-miles/40 hover:text-miles disabled:opacity-35">←</button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"
                  className="h-8 w-8 rounded-lg border border-ink/10 text-slate transition-colors hover:border-miles/40 hover:text-miles disabled:opacity-35">→</button>
              </div>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

// La prochaine course, impossible à manquer.
function NextRideCard({ b }) {
  const countdown = countdownLabel(b);
  const urgent = urgency(b) === "urgent";
  return (
    <Link to={`/admin/app/bookings/${b.id}`} data-testid="next-ride-card"
      className="group block overflow-hidden rounded-2xl bg-miles-deep p-6 text-white shadow-[0_24px_60px_-28px_rgba(11,31,77,0.55)] transition-transform duration-300 hover:-translate-y-0.5 sm:p-7">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
            {LIVE_STATUSES.includes(b.status) ? "En course" : "Prochaine course"}
          </p>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="font-mono text-3xl font-bold tracking-tight sm:text-4xl">{b.time}</p>
            <p className="text-sm text-white/70">{dayLabel(b.date)}</p>
            {countdown && (
              <span data-testid="next-ride-countdown"
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${LIVE_STATUSES.includes(b.status) ? "bg-emerald-400/15 text-emerald-300" : "bg-white/10 text-white"}`}>
                <Clock className="h-3.5 w-3.5" strokeWidth={2} /> {countdown}
              </span>
            )}
          </div>
          <div className="mt-3 text-sm text-white/85">
            <RouteLine compact dark pickup={b.pickup} dropoff={b.dropoff} />
          </div>
          <p className="mt-2 text-sm text-white/60">
            {b.customer?.first_name} {b.customer?.last_name}
            {b.flight_number ? ` · vol ${b.flight_number}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {!b.driver?.first_name && (
            <span data-testid="next-ride-no-driver"
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1.5 text-xs font-semibold text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} /> Chauffeur requis
            </span>
          )}
          {b.driver?.first_name && (
            <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white">
              {b.driver.first_name} {b.driver.last_initial || ""}
            </span>
          )}
          {isUnpaid(b) && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-400/15 px-3 py-1.5 text-xs font-semibold text-red-300">
              Paiement en attente
            </span>
          )}
          <span className={`mt-1 inline-flex items-center gap-1.5 text-xs font-semibold ${urgent ? "text-amber-300" : "text-miles-sky"}`}>
            Ouvrir la réservation <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function DriverCell({ b }) {
  if (b.driver?.first_name) return <span className="text-ink">{b.driver.first_name} {b.driver.last_initial}</span>;
  const urgent = b.date === isoToday() || b.date === isoTomorrow();
  return urgent
    ? <span className="inline-flex items-center gap-1 font-semibold text-red-600"><AlertTriangle className="h-3.5 w-3.5" strokeWidth={2} /> À assigner</span>
    : <span className="font-medium text-amber-600">Requis</span>;
}

function TimeCell({ b }) {
  const countdown = countdownLabel(b);
  return (
    <>
      <p className="font-mono text-[15px] font-semibold text-ink">{b.time}</p>
      <p className={T.meta}>
        {b.date?.slice(5).split("-").reverse().join(".")} · {b.tracking_code}
        {b.is_group_unit && (
          <span className="ml-1.5 rounded-md bg-miles-ice px-1.5 py-0.5 text-[10px] font-bold text-miles" data-testid={`group-badge-${b.tracking_code}`}>
            Groupe {b.unit_index}/{b.unit_total}
          </span>
        )}
      </p>
      {countdown && countdown !== "maintenant" && !LIVE_STATUSES.includes(b.status) && b.date === isoToday() && (
        <p className="mt-0.5 text-[11px] font-semibold text-miles">{countdown}</p>
      )}
    </>
  );
}

function BookingRow({ b }) {
  const level = urgency(b) || "none";
  return (
    <tr className={`group cursor-pointer transition-colors hover:bg-miles-ice/40 ${URGENCY_ROW[level]}`}
      onClick={() => window.location.assign(`/admin/app/bookings/${b.id}`)} data-testid={`booking-row-${b.tracking_code}`}>
      <td className="px-5 py-3.5"><TimeCell b={b} /></td>
      <td className="max-w-[300px] px-3 py-3.5"><RouteLine compact pickup={b.pickup} dropoff={b.dropoff} /></td>
      <td className="px-3 py-3.5">
        <p className="text-sm font-medium text-ink">{b.customer?.first_name} {b.customer?.last_name}</p>
        {b.source !== "web" && <p className={T.meta}>{b.source}</p>}
      </td>
      <td className="px-3 py-3.5 text-sm"><DriverCell b={b} /></td>
      <td className="px-3 py-3.5">
        <div className="flex flex-col items-start gap-1">
          <StatusBadge status={b.display_status} />
          {isUnpaid(b) && <span className="text-[11px] font-semibold text-red-600">Paiement en attente</span>}
        </div>
      </td>
      <td className="px-5 py-3.5 text-right font-mono text-sm font-medium text-ink">{money(b.price_chf)}</td>
    </tr>
  );
}

function BookingCard({ b }) {
  const level = urgency(b) || "none";
  const countdown = countdownLabel(b);
  return (
    <Link to={`/admin/app/bookings/${b.id}`} data-testid={`booking-card-${b.tracking_code}`}
      className={`${T.card} block p-4 ${URGENCY_CARD[level]}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[15px] font-semibold text-ink">{b.time} <span className="text-xs font-normal text-slate">{b.date?.slice(5).split("-").reverse().join(".")}</span></span>
        <StatusBadge status={b.display_status} />
      </div>
      {countdown && countdown !== "maintenant" && b.date === isoToday() && (
        <p className="mt-1 text-[11px] font-semibold text-miles">{countdown}</p>
      )}
      <div className="mt-2"><RouteLine compact pickup={b.pickup} dropoff={b.dropoff} /></div>
      <p className="mt-2 text-xs text-slate">{b.customer?.first_name} {b.customer?.last_name} · {b.driver?.first_name || (level === "urgent" ? "chauffeur à assigner" : "chauffeur requis")} · <span className="font-mono">{money(b.price_chf)}</span></p>
    </Link>
  );
}
